import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { LanguageProvider } from '@/contexts/LanguageContext';
import { Toaster } from '@/components/ui/sonner';
import LoginPage from '@/pages/LoginPage';
import AdminLoginPage from '@/pages/AdminLoginPage';
import Dashboard from '@/pages/Dashboard';
import ClassView from '@/pages/ClassView';
import ProfilePage from '@/pages/ProfilePage';
import ProgressPage from '@/pages/ProgressPage';
import BadgePopup from '@/components/BadgePopup';
import { useBadgeNotifications } from '@/hooks/useBadgeNotifications';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, XCircle } from 'lucide-react';
import '@/App.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="flex items-center justify-center h-screen bg-slate-50">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-slate-500 font-medium" style={{ fontFamily: 'Montserrat' }}>ActiveEnglish</p>
      </div>
    </div>
  );
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function VerifyEmail() {
  const { token } = useParams();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const verify = async () => {
      try {
        const res = await axios.get(`${API}/auth/verify-email/${token}`);
        setStatus('success');
        setMessage(res.data.message || 'Correo verificado exitosamente');
      } catch (err) {
        setStatus('error');
        setMessage(err.response?.data?.detail || 'Token de verificación inválido o expirado');
      }
    };
    if (token) verify();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center p-8 bg-white rounded-2xl shadow-lg max-w-md" data-testid="verify-email-card">
        <h1 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Montserrat', color: '#2563EB' }}>ActiveEnglish</h1>
        {status === 'loading' && <p>Verificando correo...</p>}
        {status === 'success' && (
          <div data-testid="verify-success">
            <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <p className="text-lg font-semibold text-emerald-600 mb-2">{message}</p>
            <a href="/" className="mt-4 inline-block text-blue-500 hover:underline">Ir al login</a>
          </div>
        )}
        {status === 'error' && (
          <div data-testid="verify-error">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <p className="text-lg font-semibold text-red-600 mb-2">{message}</p>
            <a href="/" className="mt-4 inline-block text-blue-500 hover:underline">Ir al login</a>
          </div>
        )}
      </div>
    </div>
  );
}

function InvitePage() {
  const { token } = useParams();
  const [invite, setInvite] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/users/invite/${token}`);
        setInvite(res.data);
      } catch {
        toast.error('Invitacion no encontrada');
      }
    };
    fetch();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center p-8 bg-white rounded-2xl shadow-lg max-w-md">
        <h1 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Montserrat', color: '#2563EB' }}>ActiveEnglish</h1>
        {invite ? (
          <div>
            <p className="text-lg mb-4">Has sido invitado como <strong>{invite.role}</strong></p>
            <a href="/" className="inline-block bg-blue-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-600 transition-colors">
              Iniciar Sesion
            </a>
          </div>
        ) : (
          <p>Cargando invitacion...</p>
        )}
      </div>
    </div>
  );
}

function BadgeNotificationLayer() {
  const { currentBadge, dismissBadge } = useBadgeNotifications();
  if (!currentBadge) return null;
  return <BadgePopup badge={currentBadge} onClose={dismissBadge} />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LanguageProvider>
          <BadgeNotificationLayer />
          <Routes>
            <Route path="/" element={<LoginPage />} />
            <Route path="/admin" element={<AdminLoginPage />} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/class/:classId" element={<ProtectedRoute><ClassView /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
            <Route path="/progreso" element={<ProtectedRoute><ProgressPage /></ProtectedRoute>} />
            <Route path="/verify-email/:token" element={<VerifyEmail />} />
            <Route path="/invite/:token" element={<InvitePage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster richColors position="top-right" />
        </LanguageProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
